/**
 * User profile component - Terminal aesthetic
 */

import { useState } from 'react';
import { useAuth } from '../hooks/useAuth';
import { updateUserProfile } from '../services/authApi';
import './UserProfile.css';

interface UserProfileProps {
  onClose: () => void;
}

export function UserProfile({ onClose }: UserProfileProps) {
  const { user, logout, refreshUser } = useAuth();
  const [isEditing, setIsEditing] = useState(false);
  const [displayName, setDisplayName] = useState(user?.display_name || '');
  const [avatarUrl, setAvatarUrl] = useState(user?.avatar_url || '');
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  if (!user) return null;

  const handleSave = async () => {
    setError('');
    setSuccess('');
    setIsSaving(true);

    try {
      await updateUserProfile({
        display_name: displayName !== user.display_name ? displayName : undefined,
        avatar_url: avatarUrl !== user.avatar_url ? avatarUrl : undefined,
      });

      // Refresh user data from backend
      await refreshUser();

      setSuccess('Profile updated successfully');
      setIsEditing(false);

      // Close modal after brief delay to show success message
      setTimeout(() => {
        onClose();
      }, 1500);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update profile');
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancel = () => {
    setDisplayName(user.display_name);
    setAvatarUrl(user.avatar_url || '');
    setIsEditing(false);
    setError('');
    setSuccess('');
  };

  const handleLogout = () => {
    logout();
    onClose();
  };

  return (
    <div className="user-profile-modal" onClick={onClose}>
      <div className="user-profile-content" onClick={(e) => e.stopPropagation()}>
        <div className="profile-header">
          <h2>&gt; USER PROFILE</h2>
          <button className="close-btn" onClick={onClose} aria-label="Close">
            X
          </button>
        </div>

        <div className="profile-body">
          {!isEditing ? (
            <div className="profile-info">
              <div className="info-row">
                <span className="info-label">USERNAME:</span>
                <span className="info-value">{user.username}</span>
              </div>
              <div className="info-row">
                <span className="info-label">DISPLAY NAME:</span>
                <span className="info-value">{user.display_name}</span>
              </div>
              <div className="info-row">
                <span className="info-label">EMAIL:</span>
                <span className="info-value">{user.email}</span>
              </div>
              <div className="info-row">
                <span className="info-label">ROLES:</span>
                <span className="info-value">{user.roles.map(r => r.toUpperCase()).join(', ')}</span>
              </div>
              <div className="info-row">
                <span className="info-label">MEMBER SINCE:</span>
                <span className="info-value">
                  {new Date(user.created_at).toLocaleDateString()}
                </span>
              </div>

              <div className="profile-actions">
                <button className="edit-btn" onClick={() => setIsEditing(true)}>
                  EDIT PROFILE
                </button>
                <button className="logout-btn" onClick={handleLogout}>
                  LOGOUT
                </button>
              </div>
            </div>
          ) : (
            <div className="profile-edit">
              <div className="form-group">
                <label htmlFor="edit-display-name">DISPLAY NAME:</label>
                <input
                  id="edit-display-name"
                  type="text"
                  value={displayName}
                  onChange={(e) => setDisplayName(e.target.value)}
                  disabled={isSaving}
                />
                <small style={{ opacity: 0.7, fontSize: '0.85em' }}>
                  Username ({user.username}) cannot be changed
                </small>
              </div>

              <div className="form-group">
                <label htmlFor="edit-avatar">AVATAR URL:</label>
                <input
                  id="edit-avatar"
                  type="text"
                  value={avatarUrl}
                  onChange={(e) => setAvatarUrl(e.target.value)}
                  placeholder="HTTPS://EXAMPLE.COM/AVATAR.JPG"
                  disabled={isSaving}
                />
              </div>

              {error && <div className="error-message">ERROR: {error}</div>}
              {success && <div className="success-message">SUCCESS: {success}</div>}

              <div className="edit-actions">
                <button
                  className="save-btn"
                  onClick={handleSave}
                  disabled={isSaving}
                >
                  {isSaving ? 'SAVING...' : 'SAVE'}
                </button>
                <button
                  className="cancel-btn"
                  onClick={handleCancel}
                  disabled={isSaving}
                >
                  CANCEL
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
